import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { MessageCircle, AlertCircle, Droplets } from 'lucide-react';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';
import FloatingWhatsApp from './FloatingWhatsApp';

const BlogPerditaAcquaLavandino = () => {
  const handleWhatsApp = () => {
    window.open('https://wa.me/393287686356?text=Ciao,%20ho%20una%20perdita%20d\'acqua%20sotto%20il%20lavandino.%20Aiuto!', '_blank');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Perdita Acqua Sotto il Lavandino: Guida Rapida | SOS Casa Roma</title>
        <meta name="description" content="Perdita d'acqua sotto il lavandino? Guida rapida per individuare e risolvere il problema. Idraulico H24 a Roma. Tel: 328 768 6356" />
        <link rel="canonical" href={`${window.location.origin}/blog/perdita-acqua-lavandino-guida`} />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Perdita Acqua Sotto il Lavandino: Guida Rapida
              </h1>
              
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  Una perdita d'acqua sotto il lavandino è un problema comune ma va risolto rapidamente per evitare danni maggiori. Ecco cosa fare subito.
                </p>

                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="h-6 w-6 text-orange-500 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-gray-800">
                      <strong>Intervento immediato:</strong> Chiudi subito il rubinetto sotto il lavandino o il rubinetto generale dell'acqua per fermare la perdita.
                    </p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Dove Si Trova la Perdita?
                </h2>
                <p className="text-gray-700 mb-4">
                  Le perdite più comuni sotto il lavandino provengono da:
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-900 flex items-center gap-2">
                      <Droplets className="h-5 w-5 text-blue-600" />
                      Sifone (tubo a "S")
                    </p>
                    <p className="text-gray-700 ml-7">Guarnizioni usurate o raccordi allentati. Prova a stringere i raccordi a mano.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-900 flex items-center gap-2">
                      <Droplets className="h-5 w-5 text-blue-600" />
                      Flessibili di alimentazione
                    </p>
                    <p className="text-gray-700 ml-7">Tubicini che portano l'acqua al rubinetto. Se vecchi, vanno sostituiti.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-900 flex items-center gap-2">
                      <Droplets className="h-5 w-5 text-blue-600" />
                      Base del rubinetto
                    </p>
                    <p className="text-gray-700 ml-7">Guarnizioni del monocomando usurate. Serve sostituzione cartuccia o rubinetto.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-900 flex items-center gap-2">
                      <Droplets className="h-5 w-5 text-blue-600" />
                      Rubinetto d'arresto
                    </p>
                    <p className="text-gray-700 ml-7">Il rubinettino sotto il lavandino perde. Va sostituito da un idraulico.</p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Cosa Fare Subito (Fai da Te)
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <div>
                      <p className="font-semibold text-gray-900">Chiudi l'acqua</p>
                      <p className="text-gray-700">Ruota il rubinetto d'arresto sotto il lavandino in senso orario fino allo stop.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <div>
                      <p className="font-semibold text-gray-900">Asciuga e individua</p>
                      <p className="text-gray-700">Asciuga tutto con uno straccio e apri leggermente l'acqua per vedere da dove perde esattamente.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <div>
                      <p className="font-semibold text-gray-900">Stringe i raccordi</p>
                      <p className="text-gray-700">Con una chiave inglese, prova a stringere delicatamente i dadi del sifone (non troppo forte!).</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">4</div>
                    <div>
                      <p className="font-semibold text-gray-900">Metti una bacinella</p>
                      <p className="text-gray-700">Se la perdita continua ma è lenta, posiziona una bacinella e chiama un idraulico.</p>
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Quando Serve l'Idraulico
                </h2>
                <p className="text-gray-700 mb-4">
                  Chiama subito SOS Casa Roma se:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>La perdita è abbondante e non si ferma</li>
                  <li>Stringere i raccordi non risolve il problema</li>
                  <li>Il sifone è rotto o molto vecchio</li>
                  <li>Perchè dall'attacco del rubinetto al lavabo</li>
                  <li>I flessibili sono gonfi o danneggiati</li>
                  <li>Non hai gli attrezzi o la sicurezza per intervenire</li>
                </ul>

                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
                  <p className="text-gray-800">
                    <strong>Prevenzione:</strong> Controlla periodicamente sotto il lavandino. Le perdite piccole diventano grandi se trascurate.
                  </p>
                </div>

                <div className="bg-blue-600 rounded-lg p-8 text-center mt-12">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Perdita Sotto il Lavandino? Interveniamo in 30 Minuti
                  </h3>
                  <p className="text-white text-lg mb-6">
                    Idraulici esperti con ricambi a bordo. Risolviamo subito perdite, sostituiamo sifoni e flessibili. Disponibili H24 a Roma.
                  </p>
                  <Button
                    onClick={handleWhatsApp}
                    size="lg"
                    className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-10 py-7 rounded-lg shadow-lg transition-all duration-300 hover:scale-105"
                  >
                    <MessageCircle className="mr-3 h-7 w-7" />
                    Chiama SOS Casa Roma su WhatsApp
                  </Button>
                  <p className="text-white mt-4 font-semibold">
                    📞 328 768 6356
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
        <FloatingWhatsApp />
      </div>
    </>
  );
};

export default BlogPerditaAcquaLavandino;
